/** Paths that should paint immediately for anonymous visitors (no auth/admin gate). */
const AUTH_ONLY_PREFIXES = ['/auth/', '/reset-password'] as const;

const AUTH_ONLY_EXACT = new Set(['/login', '/create-account', '/book-your-service']);

/**
 * True when Supabase (or similar) likely has a persisted session — wait for auth
 * before choosing dashboard vs marketing shell to avoid a flash-then-redirect.
 */
export function hasLikelyStoredSession(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      if (key.includes('auth-token') || key.includes('supabase.auth')) {
        const val = localStorage.getItem(key);
        if (val && val !== 'null' && val !== '{}' && val.length > 4) {
          return true;
        }
      }
    }
  } catch {
    /* private browsing */
  }
  return false;
}

/** Google/SSO PKCE return — must wait for code exchange before painting marketing home. */
export function hasOAuthCallbackParams(search?: string, hash?: string): boolean {
  if (typeof window === 'undefined' && search == null && hash == null) return false;
  try {
    const query = search ?? window.location.search;
    if (new URLSearchParams(query).has('code')) return true;
    const authMarker = new URLSearchParams(query).get('auth');
    if (authMarker === 'callback') return true;
    const h = (hash ?? window.location.hash).toLowerCase();
    if (h.includes('access_token=') || h.includes('error=')) return true;
    // Query-string OAuth failures from Supabase (?error=server_error&error_description=...)
    if (new URLSearchParams(query).has('error')) return true;
    return false;
  } catch {
    return false;
  }
}

function hasPendingOAuthFlag(): boolean {
  if (typeof sessionStorage === 'undefined') return false;
  try {
    return Boolean(sessionStorage.getItem('tb:oauth:pending'));
  } catch {
    return false;
  }
}

export function normalizeAppPath(pathname: string): string {
  let path = pathname.replace(/\/+$/, '') || '/';
  if (path === '/m') path = '/';
  else if (path.startsWith('/m/')) {
    path = `/${path.slice(3).replace(/^\/+/, '')}` || '/';
  }
  return path;
}

export function isAuthOnlyPath(pathname: string): boolean {
  const path = normalizeAppPath(pathname);
  if (AUTH_ONLY_EXACT.has(path)) return true;
  return AUTH_ONLY_PREFIXES.some((prefix) => path.startsWith(prefix));
}

/**
 * Anonymous marketing visitors can render while getSession() is still in flight.
 * Logged-in hints and auth-only routes still wait for auth resolution.
 *
 * A stored session holds the gate past the short timeout — abandoning it early painted
 * the marketing home page mid-refresh, which looked like a forced sign-out and left
 * dashboards empty under org-scoped RLS. `hardTimedOut` still wins, so no auth defect
 * can leave the page stuck on the loading screen indefinitely.
 */
export function shouldDeferToAuthGate(options: {
  isLoading: boolean;
  loadingTimedOut: boolean;
  hardTimedOut?: boolean;
  pathname?: string;
}): boolean {
  const { isLoading, loadingTimedOut, hardTimedOut = false, pathname = window.location.pathname } = options;
  if (!isLoading) return false;
  if (hardTimedOut) return false;
  // OAuth returns to `/` with ?code= / ?auth=callback and no session yet. Without this
  // gate the marketing homepage paints immediately and Google login looks broken.
  if (hasOAuthCallbackParams() || hasPendingOAuthFlag()) return true;
  if (hasLikelyStoredSession()) return true;
  if (loadingTimedOut) return false;
  return isAuthOnlyPath(pathname);
}
